import { useEffect, useRef, useState } from 'react';

type Props = {
  describe: string;
  children: React.ReactNode;
};

export default function Tooltip({ describe, children }: Props) {
  const [isVisible, setIsVisible] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isVisible) {
        setIsVisible(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isVisible]);

  const hideWithDelay = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setIsVisible(false), 3000);
  };

  return (
    <div className="relative inline-block">
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-describedby={describe}
        className="cursor-pointer"
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        onBlur={() => setIsVisible(false)}
        onFocus={() => setIsVisible(true)}
        onClick={() => setIsVisible((prev) => !prev)}
        onTouchEnd={(e) => {
          e.preventDefault();
          setIsVisible((prev) => !prev);
          hideWithDelay();
        }}
        tabIndex={0}
      >
        <g clipPath="url(#clip0_865_453)">
          <path
            d="M8.00016 14.6667C11.6821 14.6667 14.6668 11.6819 14.6668 8C14.6668 4.3181 11.6821 1.33333 8.00016 1.33333C4.31826 1.33333 1.3335 4.3181 1.3335 8C1.3335 11.6819 4.31826 14.6667 8.00016 14.6667Z"
            stroke="#05141F"
            strokeWidth="1.33333"
          />
          <path
            d="M8 10.6667V8"
            stroke="#05141F"
            strokeWidth="1.33333"
            strokeLinecap="square"
          />
          <rect
            x="7.3335"
            y="4.66667"
            width="1.33333"
            height="1.33333"
            fill="#05141F"
          />
        </g>
        <defs>
          <clipPath id="clip0_865_453">
            <rect width="16" height="16" fill="white" />
          </clipPath>
        </defs>
      </svg>
      <div
        className={`absolute left-1/2 transform -translate-x-1/2 bottom-full font-light text-xs rounded-md p-2 ml-1 max-w-xs ${
          isVisible ? 'visible' : 'invisible'
        }`}
        role="tooltip"
        id={describe}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
      >
        <div className="p-2 bg-black text-white rounded-md min-w-[140px]">
          {children}
        </div>
      </div>
    </div>
  );
}
